/**
 * Infinite scroll for Dawn collection product grid.
 * - Auto-loads the first N pages via IntersectionObserver
 * - Falls back to a manual "Load more" button after that, showing remaining product count
 * - Uses Section Rendering API to fetch and inject new products
 * - Updates the URL via history.pushState for shareable/bookmarkable state
 * - Leaves Dawn's real <a href> pagination links in the DOM as a crawlable fallback
 */
(function () {
  'use strict';

  class InfiniteScrollGrid {
    constructor(paginationEl) {
      this.paginationEl = paginationEl;
      this.sectionId = paginationEl.dataset.sectionId;
      this.collectionUrl = paginationEl.dataset.collectionUrl;
      this.currentPage = parseInt(paginationEl.dataset.currentPage, 10);
      this.totalPages = parseInt(paginationEl.dataset.totalPages, 10);
      this.autoLoadLimit = parseInt(paginationEl.dataset.autoLoadPages, 10) || 2;
      this.totalProducts = parseInt(paginationEl.dataset.totalProducts, 10) || 0;
      this.autoLoadCount = 0;
      this.isLoading = false;

      this.grid = document.getElementById('product-grid');
      this.sentinel = document.getElementById('InfiniteScrollSentinel-' + this.sectionId);
      this.loadingIndicator = document.getElementById('InfiniteScrollLoading-' + this.sectionId);
      this.loadMoreWrapper = document.getElementById('InfiniteScrollLoadMore-' + this.sectionId);
      this.loadMoreButton = this.loadMoreWrapper
        ? this.loadMoreWrapper.querySelector('[data-infinite-scroll-load-more]')
        : null;
      this.statusEl = document.getElementById('InfiniteScrollStatus-' + this.sectionId);

      if (!this.grid || !this.sentinel) return;

      this.productsLoaded = this.grid.children.length;

      // Mark as JS-enhanced so CSS hides the raw <nav> pagination links
      this.paginationEl.classList.add('js-enhanced');

      if (this.currentPage >= this.totalPages) return;

      this.observer = new IntersectionObserver(this.handleIntersect.bind(this), {
        rootMargin: '400px 0px 400px 0px'
      });

      this.startAutoLoad();

      if (this.loadMoreButton) {
        this.loadMoreButton.addEventListener('click', () => this.loadNextPage());
      }
    }

    startAutoLoad() {
      this.observer.observe(this.sentinel);
    }

    handleIntersect(entries) {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !this.isLoading) {
          this.loadNextPage();
        }
      });
    }

    switchToManualMode() {
      this.observer.unobserve(this.sentinel);
      this.sentinel.style.display = 'none';
      this.updateLoadMoreLabel();
      if (this.loadMoreWrapper) {
        this.loadMoreWrapper.classList.add('is-visible');
      }
    }

    setLoading(isLoading) {
      this.isLoading = isLoading;
      if (this.loadingIndicator) {
        this.loadingIndicator.classList.toggle('is-visible', isLoading);
      }
      if (this.loadMoreButton) {
        this.loadMoreButton.disabled = isLoading;
      }
    }

    updateLoadMoreLabel() {
      if (!this.loadMoreButton) return;
      const remaining = Math.max(this.totalProducts - this.productsLoaded, 0);
      if (remaining > 0) {
        this.loadMoreButton.textContent =
          'Cargar más — ' + remaining + (remaining === 1 ? ' producto restante' : ' productos restantes');
      } else {
        this.loadMoreButton.textContent = 'Cargar más productos';
      }
    }

    async loadNextPage() {
      if (this.isLoading || this.currentPage >= this.totalPages) return;

      this.setLoading(true);
      const nextPage = this.currentPage + 1;
      const fetchUrl =
        this.collectionUrl +
        '?page=' +
        nextPage +
        '&section_id=' +
        this.sectionId;

      try {
        const response = await fetch(fetchUrl);
        if (!response.ok) throw new Error('Network response was not ok');
        const text = await response.text();

        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'text/html');
        const newGrid = doc.getElementById('product-grid');
        const newPagination = doc.getElementById(
          'InfiniteScrollPagination-' + this.sectionId
        );

        if (newGrid) {
          const newItems = Array.from(newGrid.children);
          const fragment = document.createDocumentFragment();

          newItems.forEach((item) => {
            // Ensure any injected product images are lazy-loaded regardless
            // of their original position-based lazy_load flag from Liquid
            item.querySelectorAll('img').forEach((img) => {
              if (!img.hasAttribute('loading')) {
                img.setAttribute('loading', 'lazy');
              }
            });
            fragment.appendChild(item);
          });

          this.grid.appendChild(fragment);
          this.productsLoaded += newItems.length;

          if (this.statusEl) {
            this.statusEl.textContent =
              newItems.length + ' productos más cargados. Página ' + nextPage + ' de ' + this.totalPages + '.';
          }
        }

        this.currentPage = nextPage;

        // Update total pages in case product count changed since page load
        if (newPagination && newPagination.dataset.totalPages) {
          this.totalPages = parseInt(newPagination.dataset.totalPages, 10);
        }

        this.updateUrl(nextPage);
        this.updateLoadMoreLabel();
        this.autoLoadCount += 1;

        if (this.currentPage >= this.totalPages) {
          this.finish();
        } else if (this.autoLoadCount >= this.autoLoadLimit) {
          this.switchToManualMode();
        }
      } catch (error) {
        console.error('Infinite scroll load failed:', error);
        // Degrade gracefully: stop auto-loading, expose the manual button
        // and the original crawlable pagination links still work as-is
        this.switchToManualMode();
      } finally {
        this.setLoading(false);
      }
    }

    updateUrl(page) {
      const url = new URL(window.location.href);
      if (page > 1) {
        url.searchParams.set('page', page);
      } else {
        url.searchParams.delete('page');
      }
      window.history.pushState({ page: page }, '', url.toString());
    }

    finish() {
      this.observer.unobserve(this.sentinel);
      this.sentinel.style.display = 'none';
      if (this.loadMoreWrapper) {
        this.loadMoreWrapper.classList.remove('is-visible');
      }
      if (this.loadingIndicator) {
        this.loadingIndicator.classList.remove('is-visible');
      }
    }
  }

  function init() {
    document.querySelectorAll('[data-infinite-scroll]').forEach((el) => {
      new InfiniteScrollGrid(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();